import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import constants, { getUser } from "../../utils/constants";
import { base_url, fetchDataFromAPI } from "../../utils/api";
import { CSVLink } from "react-csv";
import { Button, Modal, Pagination } from "react-bootstrap";
import moment from "moment";
import Processing from "../../components/Processing/Processing";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axios from "axios";
import fileDownload from "js-file-download";

const STORAGE_KEY = "driver_archive_page_state";
const NAVIGATION_FLAG = "driver_archive_navigation_flag";

const DriverArchive = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const hasRestoredState = useRef(false);

  // Helper functions for state persistence
  const saveStateToStorage = (state) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      localStorage.setItem(NAVIGATION_FLAG, "true");
    } catch (error) {
      console.error("Error saving state to storage:", error);
    }
  };

  const loadStateFromStorage = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      const navigationFlag = localStorage.getItem(NAVIGATION_FLAG);

      // Only restore if we have saved state AND navigation flag (meaning we navigated away, not refreshed)
      if (savedState && navigationFlag === "true") {
        return JSON.parse(savedState);
      }
    } catch (error) {
      console.error("Error loading state from storage:", error);
    }
    return null;
  };

  const clearSavedState = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(NAVIGATION_FLAG);
    } catch (error) {
      console.error("Error clearing saved state:", error);
    }
  };

  // Load saved state only if coming back from navigation
  const savedState = loadStateFromStorage();

  // State to hold initial form values - this ensures formik reinitializes correctly
  const [initialFormValues, setInitialFormValues] = useState(() => {
    return savedState?.formValues
      ? {
          id: savedState.formValues.id || "",
          firstname: savedState.formValues.firstname || "",
          lastname: savedState.formValues.lastname || "",
          mobile: savedState.formValues.mobile || "",
          email: savedState.formValues.email || "",
          spsv: savedState.formValues.spsv || "",
        }
      : {
          id: "",
          firstname: "",
          lastname: "",
          mobile: "",
          email: "",
          spsv: "",
        };
  });

  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  const [selectedDeviceType, setSelectedDeviceType] = useState(
    savedState?.selectedDeviceType ?? null,
  );
  const [selectedType, setSelectedType] = useState(
    savedState?.selectedType ?? null,
  );
  const [selectedTypeSignUp, setSelectedSignUp] = useState(
    savedState?.selectedTypeSignUp ?? null,
  );

  const [sortByFieldName, setSortByFieldName] = useState(
    savedState?.sortByFieldName ?? "first_name",
  );
  const [sortOrder, setSortOrder] = useState(savedState?.sortOrder ?? "ASC");

  const [csvLoading, setCsvLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(savedState?.currentPage ?? 1);

  const [totalPages, setTotalPages] = useState(1); // Initialize total pages

  const [sortByFirstName, setSortByFirstName] = useState(
    savedState?.sortByFirstName ?? "ASC",
  );

  const [sortByLastName, setSortByLastName] = useState(
    savedState?.sortByLastName ?? "ASC",
  );

  const [selectedAppType, setSelectedAppType] = useState(
    savedState?.selectedAppType ?? "Incomplete Signups",
  );

  const getStorageKey = (type) => `driver_archive_visible_columns_${type}`;

  const getDefaultColumns = () => ({
    id: true,
    first_name: true,
    last_name: true,
    mobile_no: true,
    email: true,
    spsv: true,
    company: true,
    device_type: true,
    createdAt: true,
    clicked_to_app: true,
    agreement_version: true,
    agreement_signed: true,
    receive_service_info: true,
    vital_updates: true,
    registrationComplete: true,
    icabbiStatus: true,
    action: true,
  });

  // Column visibility state for DriverArchive
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(getStorageKey(selectedAppType));
    return saved
      ? { ...getDefaultColumns(), ...JSON.parse(saved) }
      : getDefaultColumns();
  });

  // Load correct columns whenever tab changes
  useEffect(() => {
    const saved = localStorage.getItem(getStorageKey(selectedAppType));
    setVisibleColumns(saved ? JSON.parse(saved) : getDefaultColumns());
  }, [selectedAppType]);

  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef(null);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(
        getStorageKey(selectedAppType),
        JSON.stringify(next),
      );
      return next;
    });
  };

  const resetColumns = () => {
    const defaults = getDefaultColumns();
    setVisibleColumns(defaults);
    localStorage.setItem(
      getStorageKey(selectedAppType),
      JSON.stringify(defaults),
    );
  };

  useEffect(() => {
    getDrivers(currentPage);
  }, [
    currentPage,
    selectedDeviceType,
    selectedType,
    selectedTypeSignUp,
    selectedAppType,
  ]);

  // ... Rest of your existing code ...
  const user = JSON.parse(getUser());

  const getCsv = async () => {
    const params = {
      id: user?.user_id,
      device_type: selectedDeviceType ? selectedDeviceType : null,
      clicked_to_app: selectedType,
      // page: currentPage,
      sortOrder: "DESC",
      firstName: formik.values.firstname,
      lastName: formik.values.lastname,
      email: formik.values.email,
      mobile: formik.values.mobile,
      spsv: formik.values.spsv,
      registrationComplete: selectedTypeSignUp,
      driver_status: selectedAppType,
    };

    setCsvLoading(true);

    try {
      const response = await axios.get(`${base_url}admin/exportSearchedUser`, {
        params: params,
        headers: {
          Accept: "text/csv",
          "Content-Type": "text/csv",
          "ngrok-skip-browser-warning": "true",
          Authorization: `Bearer ${user?.authToken}`,
        },
        responseType: "blob",
      });

      fileDownload(response.data, "data.csv");
      setCsvLoading(false);

      // Open the URL in a new window
      // if (url) {
      //   window.open(url, '_blank');
      // } else {
      //   console.log('URL not found in the response');
      // }
    } catch (error) {
      console.log("error", error);
      setCsvLoading(false);
    }
  };

  const getDrivers = async (pageNumber) => {
    setLoading(true);
    const params = {
      id: user?.user_id,
      uid: formik.values.id,
      device_type: selectedDeviceType,
      // sortField: sortByFieldName,
      sortOrder: "DESC",
      clicked_to_app: selectedType,
      page: pageNumber,
      firstName: formik.values.firstname,
      lastName: formik.values.lastname,
      email: formik.values.email,
      mobile: formik.values.mobile,
      spsv: formik.values.spsv,
      registrationComplete: selectedTypeSignUp,
      driver_status: selectedAppType,
    };

    axios({
      url: `${base_url}admin/users`,
      params: params,
      method: "get",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
        Authorization: `Bearer ${user?.authToken}`,
      },
    })
      // fetchDataFromAPI(`admin/users`, 'post', body, '')
      .then((response) => {
        setDrivers(response?.data?.data);
        setTotalPages(response?.data?.totalPages);

        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        // if (error?.response?.data?.status === 500) {
        //   localStorage.removeItem(constants.USER);
        //   navigate('/login');
        // }
        // toast.error(error?.response?.data?.message, {
        //   position: toast.POSITION.TOP_RIGHT,
        //   className: 'bg-danger text-white',
        // });

        console.log("error", error);
      });
    // fetchDataFromAPI(
    //   `admin/users?device_type=&page=${pageNumber}&id=${user?._id}`,
    //   'get',
    //   '',
    //   user?.authToken,
    // )
    //   .then((res) => {
    //     console.log('users res: ', res);
    //     setDrivers(res?.data);
    //     setTotalPages(res?.totalPages); // Set total pages from API response
    //     setLoading(false);
    //   })
    //   .catch((error) => {
    //     setLoading(false);
    //     console.log('users error: ', error);
    //   });
  };

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema: Yup.object({
      id: Yup.string().notRequired(),
      firstname: Yup.string().notRequired(),
      lastname: Yup.string().notRequired(),
      mobile: Yup.string().notRequired(),
      // email: Yup.string().email('Invalid email format').notRequired(),
      email: Yup.string().notRequired(),
      spsv: Yup.string().notRequired(),
    }),
    onSubmit: (values) => {
      const { firstname, lastname, mobile, email, spsv, id } = values;

      const user = JSON.parse(getUser());
      getDrivers(1);
    },
    enableReinitialize: true,
  });

  // Helper function to navigate and save state before navigating
  const navigateWithStateSave = (path, options = {}) => {
    const stateToSave = {
      selectedDeviceType,
      selectedType,
      selectedTypeSignUp,
      sortByFieldName,
      sortOrder,
      currentPage,
      sortByFirstName,
      sortByLastName,
      selectedAppType,
      formValues: {
        id: formik.values.id,
        firstname: formik.values.firstname,
        lastname: formik.values.lastname,
        mobile: formik.values.mobile,
        email: formik.values.email,
        spsv: formik.values.spsv,
      },
    };
    saveStateToStorage(stateToSave);
    navigate(path, options);
  };

  // Restore state on mount if coming back from navigation, then clear the flag
  useEffect(() => {
    // Only restore if we have saved state and haven't restored yet
    if (savedState && !hasRestoredState.current) {
      hasRestoredState.current = true;

      // Update initial form values state - this will trigger formik to reinitialize
      if (savedState.formValues) {
        setInitialFormValues({
          id: savedState.formValues.id || "",
          firstname: savedState.formValues.firstname || "",
          lastname: savedState.formValues.lastname || "",
          mobile: savedState.formValues.mobile || "",
          email: savedState.formValues.email || "",
          spsv: savedState.formValues.spsv || "",
        });
      }

      // Also set formik values directly as a backup
      if (savedState.formValues && formik && formik.setValues) {
        formik.setValues(
          {
            id: savedState.formValues.id || "",
            firstname: savedState.formValues.firstname || "",
            lastname: savedState.formValues.lastname || "",
            mobile: savedState.formValues.mobile || "",
            email: savedState.formValues.email || "",
            spsv: savedState.formValues.spsv || "",
          },
          false,
        );
      }

      // Clear the navigation flag after restoring
      clearSavedState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close Edit Columns dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target)
      ) {
        setShowColumnDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSortBy = (field) => {
    const order =
      field === sortByFieldName && sortOrder === "ASC" ? "DESC" : "ASC";
    setSortOrder(order);
    setSortByFieldName(field);

    // Perform sorting based on the selected field
    const sortedDrivers = [...drivers].sort((a, b) => {
      const fieldA = a[field].toLowerCase();
      const fieldB = b[field].toLowerCase();

      if (order === "ASC") {
        return fieldA.localeCompare(fieldB);
      } else {
        return fieldB.localeCompare(fieldA);
      }
    });

    // Update the state with the sorted data
    setDrivers(sortedDrivers);
  };

  const deleteDrivers = async () => {
    const user = JSON.parse(getUser());
    const params = {
      id: selectedDriverId,
    };
    try {
      const response = await axios.delete(`${base_url}admin/deleteUser`, {
        params: params,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.authToken}`,
        },
      });
      toast.success("Driver delete successfully ✅", {
        position: toast.POSITION.TOP_RIGHT,
        className: "bg-success text-white",
      });
      setShowDeleteModal(false);
      getDrivers();
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: toast.POSITION.TOP_RIGHT,
        className: "bg-danger text-white",
      });
      console.log("error", error);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    // getSneakerData(searchTerm?.toLowerCase(), pageNumber)
  };

  const handlePrevious = () => {
    if (currentPage !== 1) {
      setCurrentPage((prev) => prev - 1);
      getDrivers(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage !== totalPages) {
      setCurrentPage((prev) => prev + 1);
      getDrivers(currentPage + 1);
    }
  };

  const getPaginationItems = () => {
    const paginationItems = [];
    // console.log(total)

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 2) {
        paginationItems.push(
          <Pagination.Item
            key={i}
            onClick={() => handlePageClick(i)}
            active={i === currentPage}
            role="button"
          >
            {i}
          </Pagination.Item>,
        );
      } else if (paginationItems[paginationItems.length - 1] !== "...") {
        paginationItems.push("...");
      }
    }

    return paginationItems;
  };

  // Function to handle sorting by First Name
  const handleSortByFirstName = () => {
    const order = sortByFirstName === "ASC" ? "DESC" : "ASC";
    setSortByFirstName(order);
    setSortByLastName("ASC"); // Reset sorting for other columns if needed

    // Perform sorting based on First Name
    const sortedDrivers = [...drivers].sort((a, b) => {
      if (order === "ASC") {
        return a.first_name.localeCompare(b.first_name);
      } else {
        return b.first_name.localeCompare(a.first_name);
      }
    });

    // Update the state with the sorted data
    setDrivers(sortedDrivers);
  };

  // Function to handle sorting by Last Name
  const handleSortByLastName = () => {
    const order = sortByLastName === "ASC" ? "DESC" : "ASC";
    setSortByLastName(order);
    setSortByFirstName("ASC"); // Reset sorting for other columns if needed

    // Perform sorting based on Last Name
    const sortedDrivers = [...drivers].sort((a, b) => {
      if (order === "ASC") {
        return a.last_name.localeCompare(b.last_name);
      } else {
        return b.last_name.localeCompare(a.last_name);
      }
    });

    // Update the state with the sorted data
    setDrivers(sortedDrivers);
  };

  const showDeleteConfirmation = (id) => {
    setSelectedDriverId(id);
    setShowDeleteModal(true);
  };

  const handleLynkAppClick = () => {
    setSelectedAppType("Incomplete Signups");
  };

  // Event handler for selecting Lynk & iCabbi App
  const handleLynkiCabbiAppClick = () => {
    setSelectedAppType("Ex-Drivers");
  };

  const COLUMN_OPTIONS = [
    { key: "id", label: "Id" },
    { key: "first_name", label: "First Name" },
    { key: "last_name", label: "Last Name" },
    { key: "mobile_no", label: "Mobile" },
    { key: "email", label: "Email" },
    { key: "spsv", label: "SPSV" },
    { key: "company", label: "Company" },
    { key: "device_type", label: "Android/iOS" },
    { key: "createdAt", label: "Registration Timestamp" },
    { key: "clicked_to_app", label: "Clicked to App" },
    { key: "agreement_version", label: "Agreement Version" },
    { key: "agreement_signed", label: "Date Accepted" },
    { key: "receive_service_info", label: "Service Info" },
    { key: "vital_updates", label: "Vital Updates" },
    { key: "registrationComplete", label: "Sign Up Complete" },
    { key: "icabbiStatus", label: "iCabbi" },
    { key: "action", label: "Action" },
  ];

  return (
    <div id="main" className="main">
      {loading && <Processing />}
      <div className="pagetitle">
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Drivers</li>
          </ol>
        </nav>
      </div>
      <div className="card">
        <form className="mx-3" onSubmit={formik.handleSubmit}>
          <div className="row mb-3"></div>
          <div className="row mb-3 align-items-end">
            <div className="col-md-1">
              <label htmlFor="id" className="form-label">
                ID
              </label>
              <input
                name="id"
                type="text"
                className="form-control"
                id="id"
                value={formik.values.id || ""}
                onChange={(e) => {
                  formik.setFieldValue("id", e.target.value?.trimStart());
                }}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="firstName" className="form-label">
                First Name
              </label>
              <input
                name="firstName"
                type="text"
                className="form-control"
                id="firstName"
                value={formik.values.firstname || ""}
                onChange={(e) => {
                  formik.setFieldValue(
                    "firstname",
                    e.target.value?.trimStart(),
                  );
                }}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="lastName" className="form-label">
                Last Name
              </label>
              <input
                name="lastName"
                type="text"
                className="form-control"
                id="lastName"
                value={formik.values.lastname || ""}
                onChange={(e) => {
                  formik.setFieldValue("lastname", e.target.value?.trimStart());
                }}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="Mobile" className="form-label">
                Mobile
              </label>
              <input
                name="Mobile"
                type="text"
                className="form-control"
                id="Mobile"
                value={formik.values.mobile || ""}
                onChange={(e) => {
                  const trimmedValue = e.target.value.trim(); // Trim the input value
                  formik.setFieldValue("mobile", trimmedValue);
                }}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="Email" className="form-label">
                Email
              </label>
              <input
                name="Email"
                type="text"
                className="form-control"
                id="Email"
                value={formik.values.email || ""}
                onChange={(e) => {
                  formik.setFieldValue("email", e.target.value?.trimStart());
                }}
              />
            </div>
            <div className="col-md-1">
              <label htmlFor="spsv" className="form-label">
                SPSV
              </label>
              <input
                name="spsv"
                type="text"
                className="form-control"
                id="spsv"
                value={formik.values.spsv || ""}
                onChange={(e) => {
                  formik.setFieldValue("spsv", e.target.value?.trimStart());
                }}
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button
                type="submit"
                className="btn btn-warning w-100 btn-serach"
              >
                Search
              </button>
            </div>
          </div>

          {/* Export & Edit Columns row */}
          <div className="d-flex justify-content-end align-items-center gap-2 mb-3 ml-3">
            {!csvLoading ? (
              <div onClick={() => getCsv()}>
                <img
                  role="button"
                  style={{ height: 20, width: 25 }}
                  src={require("../../assets/images/export.png")}
                  alt="logo"
                />
              </div>
            ) : (
              <span className="indicator-progress" style={{ display: "block" }}>
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}

            {/* Edit Columns dropdown */}
            <div ref={columnDropdownRef} style={{ position: "relative" }}>
              <button
                type="button"
                className="btn btn-warning d-flex align-items-center gap-2"
                style={{
                  borderRadius: "10px",
                }}
                onClick={() => setShowColumnDropdown((prev) => !prev)}
              >
                <i
                  className="bi bi-layout-text-window-reverse"
                  style={{ fontSize: "1.1rem" }}
                ></i>
                Edit Columns
              </button>
              {showColumnDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    zIndex: 1050,
                    minWidth: 250,
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    padding: "10px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                    <span className="fw-bold">
                      <i className="bi bi-grid-3x3-gap"></i> Edit Columns
                    </span>
                    <span
                      role="button"
                      className="text-primary small"
                      onClick={resetColumns}
                    >
                      Reset
                    </span>
                  </div>
                  <div
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {COLUMN_OPTIONS.map((col) => (
                      <label
                        key={col.key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "6px 14px",
                          cursor: "pointer",
                          gap: 8,
                          fontSize: 14,
                        }}
                        className="column-toggle-item"
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#fff8e1")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[col.key]}
                          onChange={() => toggleColumn(col.key)}
                          style={{
                            accentColor: "#ffc107",
                            width: 16,
                            height: 16,
                          }}
                        />
                        {col.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
        <div className="card-body">
          {/* <h3 className=" font-weight-bold">Drivers List</h3> */}
          {/* <hr /> */}

          <ul className="nav nav-tabs nav-tabs-bordered">
            <li className="nav-item">
              <button
                className={`nav-link ${
                  selectedAppType === "Incomplete Signups" ? "active" : ""
                }`}
                onClick={handleLynkAppClick}
                data-bs-toggle="tab"
                data-bs-target="#driver-app"
              >
                Incomplete Signups
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${
                  selectedAppType === "Ex-Drivers" ? "active" : ""
                }`}
                onClick={handleLynkiCabbiAppClick}
                data-bs-toggle="tab"
                data-bs-target="#driver-icabbi"
              >
                Ex-Drivers
              </button>
            </li>
          </ul>
          {/* <!-- Default Table --> */}
          <div className="tab-content pt-2 ">
            <div className="table-responsive">
              <table className="table text-nowrap align-items-end table-hover">
                <thead>
                  <tr className="">
                    {visibleColumns.id && <th scope="col">Id</th>}
                    {visibleColumns.first_name && (
                      <th
                        scope="col"
                        role="button"
                        onClick={() => handleSortBy("first_name")}
                      >
                        First Name{" "}
                        {sortByFieldName === "first_name" && sortOrder === "ASC"
                          ? "▲"
                          : "▼"}
                      </th>
                    )}

                    {visibleColumns.last_name && (
                      <th
                        scope="col"
                        role="button"
                        onClick={() => handleSortBy("last_name")}
                      >
                        Last Name{" "}
                        {sortByFieldName === "last_name" && sortOrder === "ASC"
                          ? "▲"
                          : "▼"}
                      </th>
                    )}
                    {visibleColumns.mobile_no && <th scope="col">Mobile</th>}
                    {visibleColumns.email && <th scope="col">Email</th>}
                    {visibleColumns.spsv && <th scope="col">SPSV</th>}
                    {visibleColumns.company && <th scope="col">Company</th>}
                    {visibleColumns.device_type && (
                      <th scope="col">
                        <select
                          className="selectapp"
                          value={selectedDeviceType}
                          onChange={(e) =>
                            setSelectedDeviceType(e.target.value)
                          }
                        >
                          <option value="">Android/iOS</option>
                          <option value="Android">Android</option>
                          <option value="iOS">IOS</option>
                        </select>
                      </th>
                    )}

                    {visibleColumns.createdAt && (
                      <th scope="col">Registration Timestamp</th>
                    )}

                    {visibleColumns.clicked_to_app && (
                      <th scope="col" className="text-center">
                        <select
                          className="selectapp"
                          value={selectedType}
                          onChange={(e) => setSelectedType(e.target.value)}
                        >
                          <option value="">Clicked to App (Yes/No)</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </th>
                    )}
                    {visibleColumns.agreement_version && (
                      <th scope="col">Agreement Version</th>
                    )}
                    {visibleColumns.agreement_signed && (
                      <th scope="col">Date Accepted</th>
                    )}
                    {visibleColumns.receive_service_info && (
                      <th scope="col" className="text-center">
                        Service Info
                      </th>
                    )}
                    {visibleColumns.vital_updates && (
                      <th scope="col" className="text-center">
                        Vital Updates
                      </th>
                    )}
                    {visibleColumns.registrationComplete && (
                      <th scope="col" className="text-center">
                        Sign Up Complete (Yes/No)
                      </th>
                    )}

                    {visibleColumns.icabbiStatus && (
                      <th scope="col" className="text-center">
                        iCabbi
                      </th>
                    )}
                    {visibleColumns.action && (
                      <th scope="col" className="text-center">
                        Action
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {drivers?.map((item, index) => {
                    const isAgreementVerified =
                      item.agreement_verified === true;
                    const isDocumentUploaded = item.document_uploaded === true;
                    const displayValue =
                      isAgreementVerified && isDocumentUploaded ? "Yes" : "No";

                    const formattedDate = moment(item?.createdAt).format(
                      "YYYY-MM-DD HH:mm:ss a",
                    ); // Adjust the format as needed

                    const dareSignd = item?.agreement_signed
                      ? moment(item?.agreement_signed).format("DD-MM-YYYY")
                      : "Not Accepted Yet ";

                    return (
                      <tr
                        className=""
                        role="button"
                        onClick={() =>
                          navigateWithStateSave(
                            `/driver-archive/view/${item?.user_id}`,
                            {
                              state: {
                                user: {
                                  ...item,
                                  driver_status: selectedAppType,
                                },
                              },
                            },
                          )
                        }
                      >
                        {visibleColumns.id && (
                          <td scope="row">{item?.user_id}</td>
                        )}

                        {visibleColumns.first_name && (
                          <td>{item?.first_name}</td>
                        )}
                        {visibleColumns.last_name && <td>{item?.last_name}</td>}
                        {visibleColumns.mobile_no && (
                          <td>{item?.mobile_no ? item?.mobile_no : "-"}</td>
                        )}
                        {visibleColumns.email && <td>{item?.email}</td>}
                        {visibleColumns.spsv && <td>{item?.spsv}</td>}
                        {visibleColumns.company && (
                          <td>
                            {item?.company?.company_name
                              ? item?.company?.company_name
                              : "-"}
                          </td>
                        )}
                        {visibleColumns.device_type && (
                          <td>{item?.device_type}</td>
                        )}
                        {visibleColumns.createdAt && <td>{formattedDate}</td>}
                        {visibleColumns.clicked_to_app && (
                          <td className="text-center">
                            {item?.clicked_to_app ? item?.clicked_to_app : "No"}
                          </td>
                        )}
                        {visibleColumns.agreement_version && (
                          <td>
                            {item.agreement_version
                              ? item.agreement_version
                              : "-"}
                          </td>
                        )}
                        {visibleColumns.agreement_signed && (
                          <td>{dareSignd}</td>
                        )}
                        {visibleColumns.receive_service_info && (
                          <td className="text-center">
                            {Number(item.is_receive_service_information) === 1
                              ? "ON"
                              : "OFF"}
                          </td>
                        )}
                        {visibleColumns.vital_updates && (
                          <td className="text-center">
                            {Number(item.is_receive_vital_service_updates) === 1
                              ? "ON"
                              : "OFF"}
                          </td>
                        )}
                        {visibleColumns.registrationComplete && (
                          <td className="text-center">
                            {item?.registrationComplete}
                          </td>
                        )}

                        {visibleColumns.icabbiStatus && (
                          <td className="text-center">
                            {item?.icabbiStatus === 0 ? "Inactive" : "Active"}
                          </td>
                        )}
                        {visibleColumns.action && (
                          <td className="text-center">
                            <div className="d-flex justify-content-center ">
                              <div
                                className="me-2 "
                                role="button"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevents event propagation to the row click
                                  navigateWithStateSave(
                                    `/driver-archive/edit/${item?.user_id}`,
                                    {
                                      state: {
                                        user: {
                                          ...item,
                                          driver_status: selectedAppType,
                                        },
                                      },
                                    },
                                  );
                                }}
                              >
                                <i class="bi bi-pencil-square"></i>
                              </div>
                              <div
                                role="button"
                                className="me-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateWithStateSave(
                                    `/driver-archive/view/${item?.user_id}`,
                                    {
                                      state: {
                                        user: {
                                          ...item,
                                          driver_status: selectedAppType,
                                        },
                                      },
                                    },
                                  );
                                }}
                              >
                                <i class="bi bi-eye"></i>
                              </div>
                              {user?.type === "super_admin" && (
                                <div
                                  role="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    showDeleteConfirmation(item?.user_id);
                                  }}
                                  className=""
                                >
                                  <i className="bi bi-trash"></i>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* <!-- End Default Table Example --> */}
        </div>

        <Modal
          show={showDeleteModal}
          centered
          onHide={() => setShowDeleteModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Delete Confirmation</Modal.Title>
          </Modal.Header>
          <Modal.Body className="">
            <p className="m-0">
              Are you sure you'd like to delete this driver and all their
              records?
            </p>
          </Modal.Body>
          <Modal.Footer className="text-end gap-1 border-0 px-3 py-2">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={() => deleteDrivers()}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

        {/* <div className="col align-items-center justify-content-center   text-center ">
          {filteredDrivers?.length == 0 && (
            <h3 className="text-center ">No Data Found</h3>
          )}
        </div> */}
        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination className="align-items-center justify-content-center">
            {currentPage !== 1 && ( // Check if it's not the first page
              <p
                className="text-center mb-sm-0 pe-3 cursor-pointer"
                onClick={handlePrevious}
                role="button"
              >
                Previous
              </p>
            )}
            {getPaginationItems()}
            {currentPage !== totalPages &&
              drivers.length > 0 && ( // Check if it's not the last page and there's data
                <p
                  className="text-center mb-sm-0 ps-3 cursor-pointer"
                  onClick={handleNext}
                  role="button"
                >
                  Next
                </p>
              )}
          </Pagination>
        )}

        {totalPages <= 1 && drivers?.length === 0 && (
          <p className="text-center">No data available</p>
        )}
      </div>
    </div>
  );
};

export default DriverArchive;
